import React, { useMemo } from "react";
import { MdCheckCircle, MdPendingActions, MdTimer, MdRefresh, MdShield, MdBusiness } from "react-icons/md";
import { useGetTargetResourcesQuery } from "../../utils/api/endpoints/programsApi";
import { useSubmitRunScopeMutation, useVerifyRunScopeMutation, useRefreshRunScopeSnapshotMutation } from "../../utils/api/endpoints/reportsApi";
import toast from "react-hot-toast";
import moment from "moment";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";

interface RunScopeVerificationProps {
    run: {
        run_id: number;
        run_title: string;
        start_date: string;
        status: string;
        is_scope_verified: boolean;
        is_scope_submitted: boolean;
        settings_snapshot: string;
    };
    onActionSuccess?: () => void;
}

const RunScopeVerification: React.FC<RunScopeVerificationProps> = ({ run, onActionSuccess }) => {
    const { role } = useUserCredentials();
    const isAdmin = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role as UserRole);
    const isBusiness = [UserRole.BUSINESS, UserRole.BUSINESS_ADMINISTRATOR, UserRole.BUSINESS_MANAGER].includes(role as UserRole);

    const snapshot = useMemo(() => {
        try {
            return JSON.parse(run.settings_snapshot);
        } catch (e) {
            return null;
        }
    }, [run.settings_snapshot]);

    const [submitScope, { isLoading: isSubmitting }] = useSubmitRunScopeMutation();
    const [verifyScope, { isLoading: isVerifying }] = useVerifyRunScopeMutation();
    const [refreshSnapshot, { isLoading: isRefreshing }] = useRefreshRunScopeSnapshotMutation();

    const startDate = moment(run.start_date);
    const deadline = moment(run.start_date).subtract(2, 'days');
    const now = moment();
    const isDeadlinePassed = now.isAfter(deadline);
    const daysUntilDeadline = deadline.diff(now, 'days');

    const handleRefresh = async () => {
        try {
            await refreshSnapshot(run.run_id).unwrap();
            toast.success("Snapshot refreshed and verification reset.");
            onActionSuccess?.();
        } catch (err: any) {
            toast.error(err.data?.message || "Failed to refresh snapshot");
        }
    };

    const handleSubmit = async () => {
        try {
            await submitScope(run.run_id).unwrap();
            toast.success("Scope submitted for admin verification.");
            onActionSuccess?.();
        } catch (err: any) {
            toast.error(err.data?.message || "Failed to submit scope");
        }
    };

    const handleVerify = async () => {
        try {
            await verifyScope(run.run_id).unwrap();
            toast.success("Scope verified and finalized.");
            onActionSuccess?.();
        } catch (err: any) {
            toast.error(err.data?.message || "Failed to verify scope");
        }
    };

    if (!snapshot) return null;

    return (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            {/* Header / Status Bar */}
            <div className={`px-6 py-4 flex items-center justify-between ${run.is_scope_verified ? 'bg-emerald-50' : isDeadlinePassed ? 'bg-rose-50' : 'bg-blue-50'}`}>
                <div className="flex items-center gap-3">
                    {run.is_scope_verified ? (
                        <MdCheckCircle className="w-6 h-6 text-emerald-500" />
                    ) : (
                        <MdPendingActions className={`w-6 h-6 ${isDeadlinePassed ? 'text-rose-500' : 'text-blue-500'}`} />
                    )}
                    <div>
                        <h3 className="text-sm font-bold text-slate-900">
                            {run.is_scope_verified ? "Scope Verified & Finalized" :
                                run.is_scope_submitted ? "Waiting for Admin Verification" :
                                    "Scope Verification Required"}
                        </h3>
                        <p className="text-[10px] text-slate-500 font-medium">
                            Run starts: {startDate.format("MMM Do, YYYY")} • Run ID: #{run.run_id}
                        </p>
                    </div>
                </div>

                {!run.is_scope_verified && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDeadlinePassed ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>
                        <MdTimer className="w-4 h-4" />
                        {isDeadlinePassed ? "Deadline Passed" : `Deadline in ${daysUntilDeadline} days`}
                    </div>
                )}
            </div>

            <div className="p-6 space-y-6">
                {/* Workflow Guidance */}
                <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl border ${run.is_scope_submitted ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <MdBusiness className={`w-4 h-4 ${run.is_scope_submitted ? 'text-emerald-600' : 'text-slate-400'}`} />
                            <span className={`text-[10px] font-black uppercase tracking-widest ${run.is_scope_submitted ? 'text-emerald-700' : 'text-slate-500'}`}>1. Business Submit</span>
                        </div>
                        <p className="text-[11px] text-slate-600 leading-relaxed font-medium">Business user must verify the assets, targets, and endpoints before the deadline.</p>
                    </div>
                    <div className={`p-4 rounded-xl border ${run.is_scope_verified ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <MdShield className={`w-4 h-4 ${run.is_scope_verified ? 'text-emerald-600' : 'text-slate-400'}`} />
                            <span className={`text-[10px] font-black uppercase tracking-widest ${run.is_scope_verified ? 'text-emerald-700' : 'text-slate-500'}`}>2. Admin Finalize</span>
                        </div>
                        <p className="text-[11px] text-slate-600 leading-relaxed font-medium">After business submission, an admin must perform the final review to unlock the run.</p>
                    </div>
                </div>

                {/* Scope Preview */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Current Scope Snapshot</label>
                        {!run.is_scope_verified && !isDeadlinePassed && (isBusiness || isAdmin) && (
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-1 rounded-md transition-colors"
                            >
                                <MdRefresh className={isRefreshing ? 'animate-spin' : ''} /> Refresh Snapshot
                            </button>
                        )}
                    </div>

                    <div className="border border-slate-100 rounded-xl bg-slate-50/50 p-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-800">{snapshot.asset.name}</span>
                            <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] font-bold uppercase">{snapshot.asset.type}</span>
                        </div>

                        <div className="space-y-3">
                            {snapshot.targets.map((target: any) => (
                                <div key={target.target_id} className="bg-white border border-slate-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-slate-700">{target.target_name}</span>
                                        <span className="text-[9px] text-slate-400 font-medium flex items-center gap-2">
                                            {(!(target.target_value === "Uploaded Binary" || target.target_value === target.metadata?.binary_name) || !target.metadata?.binary_url) && target.target_value}
                                            {(target.metadata?.binary_name && target.metadata?.binary_url) && (
                                                <a
                                                    href={target.metadata.binary_url}
                                                    download={target.metadata.binary_name}
                                                    className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                                                    title={`Download ${target.metadata.binary_name}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    {target.metadata.binary_name}
                                                </a>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {target.endpoints.map((ep: any) => (
                                            <span key={ep.resource_id} className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 text-[9px] font-medium">
                                                {ep.value}
                                            </span>
                                        ))}
                                        {target.endpoints.length === 0 && <span className="text-[9px] text-slate-400 italic">No specific endpoints defined</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                    {/* Business Submission Button */}
                    {!run.is_scope_verified && !run.is_scope_submitted && isBusiness && (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || isDeadlinePassed}
                            className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition-all shadow-md shadow-blue-200"
                        >
                            {isSubmitting ? "Submitting..." : "Submit Scope for Final Review"}
                        </button>
                    )}

                    {/* Admin Verification Button */}
                    {!run.is_scope_verified && run.is_scope_submitted && isAdmin && (
                        <button
                            onClick={handleVerify}
                            disabled={isVerifying}
                            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-md shadow-emerald-200"
                        >
                            {isVerifying ? "Verifying..." : "Approve & Finalize Scope"}
                        </button>
                    )}

                    {run.is_scope_verified && (
                        <div className="bg-emerald-100 text-emerald-700 px-6 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2">
                            <MdCheckCircle className="w-4 h-4" /> Scope Finalized
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RunScopeVerification;
